import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { Checkbox } from "@/components/ui/checkbox";
import Spinner from "@/components/Spinner";
import toast from "react-hot-toast";
import { actionLabel, domainLabel, sortActions } from "@/lib/permissions";
import {
  useCreateAccessRoleMutation,
  useGetPermissionsQuery,
  useUpdateAccessRoleMutation,
} from "@/redux/services/permission/accessRole.api";
import { toastApiError } from "@/lib/apiErrors";
import {
  AccessRole,
  AccessRoleScope,
} from "@/redux/services/permission/permission.type";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  scope: AccessRoleScope;
  role?: AccessRole;
}

const AccessRoleFormModal: React.FC<Props> = ({
  isOpen,
  onClose,
  scope,
  role,
}) => {
  const { data: catalog, isLoading: catalogLoading } = useGetPermissionsQuery();
  const [createAccessRole, { isLoading: isCreating }] =
    useCreateAccessRoleMutation();
  const [updateAccessRole, { isLoading: isUpdating }] =
    useUpdateAccessRoleMutation();

  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(role?.permissions ?? [])
  );
  const [nameError, setNameError] = useState<string>();

  const isLoading = isCreating || isUpdating;

  // Catalog-driven: each domain exposes its own action set (view/manage, or
  // cash_register's view/open/close/movement). The view action is treated as
  // the gate — every other action implies it.
  const domains = useMemo(
    () =>
      (catalog ?? []).map((d) => ({
        domain: d.domain,
        actions: sortActions(d.permissions),
        viewValue: d.permissions.find((p) => p.action === "view")?.value,
      })),
    [catalog]
  );

  // Toggles a single permission, enforcing the "non-view implies view" rule:
  // selecting any action also selects the domain's view; clearing view clears
  // every other action in that domain.
  const togglePermission = (
    value: string,
    action: string,
    domainActions: { value: string }[],
    viewValue?: string
  ) => {
    const next = new Set(selected);
    const isView = action === "view";
    if (next.has(value)) {
      next.delete(value);
      if (isView) domainActions.forEach((p) => next.delete(p.value));
    } else {
      next.add(value);
      if (!isView && viewValue) next.add(viewValue);
    }
    setSelected(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError(undefined);

    if (!name.trim()) {
      setNameError("Le nom est obligatoire.");
      return;
    }
    if (selected.size === 0) {
      toast.error("Sélectionnez au moins une permission.");
      return;
    }

    const data = {
      name: name.trim(),
      description: description.trim() || undefined,
      permissions: Array.from(selected),
    };

    try {
      if (role) {
        await updateAccessRole({ scope, id: role.id, data }).unwrap();
        toast.success("Rôle mis à jour.");
      } else {
        await createAccessRole({ scope, data }).unwrap();
        toast.success("Rôle créé.");
      }
      onClose();
    } catch (error) {
      toastApiError(error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader className="mb-6">
            <DialogTitle>
              {role ? "Modifier le rôle d'accès" : "Nouveau rôle d'accès"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              label="Nom"
              id="access-role-name"
              type="text"
              placeholder="Ex. Responsable inventaire"
              required
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              error={nameError}
            />
            <Input
              label="Description"
              id="access-role-description"
              type="text"
              placeholder="Description (optionnelle)"
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
            />

            <div>
              <p className="text-sm font-medium text-card-foreground mb-2">
                Permissions
              </p>
              {catalogLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  {domains.map(({ domain, actions, viewValue }) => (
                    <div
                      key={domain}
                      className="flex flex-col gap-2 px-4 py-3 border-t border-border first:border-t-0 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <span className="text-sm font-medium text-card-foreground">
                        {domainLabel(domain)}
                      </span>
                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                        {actions.map((p) => (
                          <label
                            key={p.value}
                            className="flex cursor-pointer items-center gap-2"
                          >
                            <Checkbox
                              checked={selected.has(p.value)}
                              onCheckedChange={() =>
                                togglePermission(
                                  p.value,
                                  p.action,
                                  actions,
                                  viewValue
                                )
                              }
                              aria-label={`${domainLabel(domain)} - ${actionLabel(
                                p.action
                              )}`}
                            />
                            <span className="text-sm text-card-foreground">
                              {actionLabel(p.action)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Toute action accorde automatiquement « Consulter ».
              </p>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" isLoading={isLoading}>
              {role ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AccessRoleFormModal;
